import { useRef, useState } from 'react'
import {
  useFloating,
  useHover,
  useFocus,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
  FloatingArrow,
  arrow,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react'
import { Z_INDEX } from 'styles/z-index'

type TooltipTheme = 'light' | 'dark'
type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

interface FloatingTooltipProps {
  children: React.ReactElement
  text: string
  theme?: TooltipTheme
  placement?: TooltipPlacement
  /** Open delay in ms (close is instant). 200 by default — prevents flicker
   *  when the cursor sweeps across dense interactive clusters. */
  delay?: number
}

export function FloatingTooltip({
  children,
  text,
  theme = 'light',
  placement = 'top',
  delay = 200,
}: FloatingTooltipProps) {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)
  const isDark = theme === 'dark'
  const bg = isDark ? '#1f2937' : '#ffffff'

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  })

  const hover = useHover(context, {
    move: false,
    delay: { open: delay, close: 0 },
  })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ])

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className="inline-flex"
      >
        {children}
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: Z_INDEX.TOOLTIP }}
            className={[
              'px-3 py-2 rounded-lg text-sm font-medium leading-5 tracking-[-0.2px] max-w-[220px]',
              isDark
                ? 'bg-neutral-800 text-white'
                : 'bg-white text-neutral-700',
              !isDark && 'shadow-[0_1px_2px_rgba(16,24,40,0.05)]',
            ]
              .filter(Boolean)
              .join(' ')}
            {...getFloatingProps()}
          >
            {text}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={bg}
              width={12}
              height={6}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
